import { useCallback, useEffect, useMemo, useState } from "react";
import { guardarVarias, listaNinos, listar, rpc } from "../../services/datos";
import Selector from "../../shared/components/Selector";

export const ASPECTOS = [
  { id: "asistio", label: "Asistencia" },
  { id: "tarea", label: "Tarea" },
  { id: "biblia", label: "Biblia" },
  { id: "cuaderno", label: "Cuaderno" },
];

const input = "campo w-auto";
const fecha = (valor) => (valor ? new Date(`${valor}T00:00:00`).toLocaleDateString("es-CO") : "Sin fecha");

// Servicio que se propone por defecto: la clase más reciente que ya ocurrió.
export function servicioPorDefecto(servicios, hoy) {
  const pasados = servicios.filter((s) => s.fecha && s.fecha <= hoy).sort((a, b) => b.fecha.localeCompare(a.fecha));
  return pasados[0] || servicios[0] || null;
}

export function porcentaje(parte, total) {
  return total ? Math.round((parte / total) * 100) : 0;
}

function CalificacionesNinos() {
  const [tab, setTab] = useState("calificar");
  const [servicios, setServicios] = useState([]);
  const [ninos, setNinos] = useState([]);
  const [servicioId, setServicioId] = useState("");
  const [filas, setFilas] = useState({});
  const [resumen, setResumen] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    Promise.all([listar("servicios", { select: "*, tipos_servicio(nombre, dirigido_a)", orden: "fecha" }), listaNinos()])
      .then(([serviciosData, ninosData]) => {
        setServicios(serviciosData);
        setNinos(ninosData.filter((n) => n.activo !== false));
        const inicial = servicioPorDefecto(serviciosData, new Date().toLocaleDateString("en-CA"));
        if (inicial) setServicioId(String(inicial.id));
      })
      .catch((error) => setMensaje(error.message));
  }, []);

  const cargarCalificaciones = useCallback(async () => {
    if (!servicioId) return;
    try {
      const guardadas = await listar("calificaciones_ninos", { filtros: { servicio_id: Number(servicioId) } });
      setFilas(Object.fromEntries(guardadas.map((c) => [c.nino_id, c])));
    } catch (error) {
      setMensaje(error.message);
    }
  }, [servicioId]);

  useEffect(() => { cargarCalificaciones(); }, [cargarCalificaciones]);

  useEffect(() => {
    if (tab === "resumen") rpc("calificaciones_resumen").then((d) => setResumen(d || [])).catch((e) => setMensaje(e.message));
  }, [tab]);

  const cambiar = (ninoId, campo, valor) =>
    setFilas((actual) => ({ ...actual, [ninoId]: { ...actual[ninoId], [campo]: valor } }));

  const marcarColumna = (aspecto) => {
    const todos = ninos.every((n) => filas[n.id]?.[aspecto]);
    setFilas((actual) => Object.fromEntries(
      ninos.map((n) => [n.id, { ...actual[n.id], [aspecto]: !todos }])
    ));
  };

  const guardar = async () => {
    setGuardando(true);
    setMensaje("");
    try {
      const registros = ninos.map((n) => ({
        servicio_id: Number(servicioId),
        nino_id: n.id,
        notas: filas[n.id]?.notas || "",
        ...Object.fromEntries(ASPECTOS.map((a) => [a.id, Boolean(filas[n.id]?.[a.id])])),
      }));
      // Quien califica queda anotado en la base.
      await guardarVarias("calificaciones_ninos", registros, "servicio_id,nino_id");
      setMensaje(`Se guardaron las calificaciones de ${registros.length} niño(s).`);
      cargarCalificaciones();
    } catch (error) {
      setMensaje(error.message);
    } finally {
      setGuardando(false);
    }
  };

  const ordenados = useMemo(
    () => [...ninos].sort((a, b) => `${a.nombres} ${a.apellidos}`.localeCompare(`${b.nombres} ${b.apellidos}`)),
    [ninos]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="titulo-pagina">Calificaciones</h2>
        <p className="text-gray-400 text-sm mt-1">Asistencia, tarea, Biblia y cuaderno de cada niño en cada clase.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[["calificar", "Calificar clase"], ["resumen", "Resumen por niño"]].map(([id, etiqueta]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`pestana ${tab === id ? "pestana-activa" : ""}`}>
            {etiqueta}
          </button>
        ))}
      </div>

      {mensaje && <p className="mb-4 text-sky-200 text-sm">{mensaje}</p>}

      {tab === "calificar" ? (
        <section className="panel p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-5">
            <label className="block">
              <span className="etiqueta">Clase</span>
              <Selector value={servicioId} onChange={(e) => setServicioId(e.target.value)} className={`${input} w-full md:w-96`}>
                {servicios.map((s) => (
                  <option key={s.id} value={s.id}>{fecha(s.fecha)} · {s.tipos_servicio?.nombre || s.titulo || "Servicio"}</option>
                ))}
              </Selector>
            </label>
            <button onClick={guardar} disabled={guardando || !servicioId || ninos.length === 0}
              className="btn-primario">
              {guardando ? "Guardando..." : "Guardar calificaciones"}
            </button>
          </div>

          {ordenados.length === 0 ? (
            <p className="text-gray-400">No hay niños activos registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="py-2 pr-3">Niño</th>
                    {ASPECTOS.map((a) => (
                      <th key={a.id} className="py-2 px-2 text-center">
                        <button type="button" onClick={() => marcarColumna(a.id)} className="hover:text-dorado" title="Marcar o desmarcar a todos">
                          {a.label}
                        </button>
                      </th>
                    ))}
                    <th className="py-2 pl-3">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenados.map((n) => (
                    <tr key={n.id} className="border-b border-gray-800">
                      <td className="py-2 pr-3 text-white">{n.nombres} {n.apellidos}</td>
                      {ASPECTOS.map((a) => (
                        <td key={a.id} className="py-2 px-2 text-center">
                          <input type="checkbox" aria-label={`${a.label} de ${n.nombres}`} checked={Boolean(filas[n.id]?.[a.id])}
                            onChange={(e) => cambiar(n.id, a.id, e.target.checked)} className="h-5 w-5 accent-yellow-500" />
                        </td>
                      ))}
                      <td className="py-2 pl-3">
                        <input value={filas[n.id]?.notas || ""} onChange={(e) => cambiar(n.id, "notas", e.target.value)}
                          className={`${input} w-full py-2`} placeholder="Opcional" maxLength={500} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="panel p-5 overflow-x-auto">
          {resumen.length === 0 ? (
            <p className="text-gray-400">Aún no hay clases calificadas.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="py-2 pr-3">Niño</th>
                  <th className="py-2 px-2 text-center">Clases</th>
                  {ASPECTOS.map((a) => <th key={a.id} className="py-2 px-2 text-center">{a.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {resumen.map((fila) => (
                  <tr key={fila.nino_id} className="border-b border-gray-800">
                    <td className="py-2 pr-3 text-white">{fila.nombres} {fila.apellidos}</td>
                    <td className="py-2 px-2 text-center text-gray-300">{fila.clases}</td>
                    {ASPECTOS.map((a) => (
                      <td key={a.id} className="py-2 px-2 text-center text-gray-300">
                        {fila[a.id]} <span className="text-gray-500">({porcentaje(fila[a.id], fila.clases)}%)</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

export default CalificacionesNinos;
